import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { campaignAPI } from '../services/api';

const s = {
  page: { maxWidth: 720, margin: '0 auto', padding: '4rem 2rem' },
  title: { fontFamily: 'Fraunces, serif', fontSize: '2.2rem', fontWeight: 600, marginBottom: '0.5rem', color: '#1c1814' },
  sub: { color: '#7a6f65', marginBottom: '2.5rem' },
  card: { background: '#fff', border: '1px solid #e2dbd0', borderRadius: 20, padding: '2.5rem', boxShadow: '0 4px 20px rgba(0,0,0,0.07)' },
  section: { marginBottom: '2rem' },
  sectionTitle: { fontWeight: 600, fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#c0392b', marginBottom: '1.25rem' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#3d3530', marginBottom: '0.4rem' },
  input: { width: '100%', padding: '0.75rem 1rem', border: '1.5px solid #e2dbd0', borderRadius: 10, fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit', background: '#faf7f2', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '0.75rem 1rem', border: '1.5px solid #e2dbd0', borderRadius: 10, fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit', background: '#faf7f2', resize: 'vertical', minHeight: 140, boxSizing: 'border-box' },
  hint: { fontSize: '0.75rem', color: '#9c9189', marginTop: '0.3rem' },
  step: { display: 'flex', gap: '1rem', alignItems: 'flex-start', padding: '1rem', background: '#faf7f2', borderRadius: 10, marginBottom: '0.75rem' },
  stepNum: { width: 28, height: 28, borderRadius: '50%', background: '#c0392b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 },
  stepText: { fontSize: '0.85rem', color: '#3d3530', lineHeight: 1.6 },
  btn: { width: '100%', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 10, padding: '1rem', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginTop: '1rem' },
  error: { background: '#fdf0ee', color: '#c0392b', padding: '0.85rem 1rem', borderRadius: 8, marginBottom: '1.5rem', fontSize: '0.875rem' },
  success: { background: '#edf7f2', color: '#1e6f3e', padding: '1.25rem', borderRadius: 10, textAlign: 'center', fontSize: '0.95rem' },
};

export default function CreateCampaign() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [campaignId, setCampaignId] = useState(null);
  const [kycFile, setKycFile] = useState(null);

  const [form, setForm] = useState({
    title: '', story: '', formerCompany: '', formerRole: '',
    layoffDate: '', goalAmount: '', deadline: '', currency: 'INR',
    linkedinUrl: '', city: '', country: 'India',
  });

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await campaignAPI.create({
        ...form,
        goalAmount: parseFloat(form.goalAmount),
      });
      setCampaignId(data.id);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create campaign. Please try again.');
    } finally { setLoading(false); }
  };

  const handleKycUpload = async (e) => {
    e.preventDefault();
    if (!kycFile) { setError('Please select a document to upload.'); return; }
    setError(''); setLoading(true);
    try {
      await campaignAPI.uploadKyc(campaignId, kycFile);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally { setLoading(false); }
  };

  if (step === 3) return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
          <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', marginBottom: '1rem', color: '#1c1814' }}>
            Campaign submitted!
          </h2>
          <p style={{ color: '#7a6f65', marginBottom: '2rem', lineHeight: 1.8 }}>
            Your campaign is under review. Our team will verify your documents and approve it within <strong>24 hours</strong>.
            You'll receive an email notification once it's live.
          </p>
          <button style={s.btn} onClick={() => navigate('/dashboard')}>
            Go to Dashboard →
          </button>
        </div>
      </div>
    </div>
  );

  if (step === 2) return (
    <div style={s.page}>
      <h1 style={s.title}>Upload Verification</h1>
      <p style={s.sub}>Upload your layoff letter or severance document to get verified.</p>
      <div style={s.card}>
        {error && <div style={s.error}>{error}</div>}
        <div style={s.section}>
          <div style={s.sectionTitle}>Accepted documents</div>
          {['Layoff/termination letter from your employer', 'Severance agreement', 'Official HR communication about your layoff'].map((t, i) => (
            <div key={i} style={s.step}>
              <div style={s.stepNum}>✓</div>
              <div style={s.stepText}>{t}</div>
            </div>
          ))}
        </div>
        <form onSubmit={handleKycUpload}>
          <div style={s.field}>
            <label style={s.label}>Upload document (PDF, JPG, PNG — max 10MB)</label>
            <input style={{ ...s.input, padding: '0.6rem' }} type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={e => setKycFile(e.target.files[0])} required />
            <div style={s.hint}>Your document is encrypted and only seen by our verification team.</div>
          </div>
          <button style={s.btn} type="submit" disabled={loading}>
            {loading ? 'Uploading...' : 'Submit for verification →'}
          </button>
          <button type="button" style={{ ...s.btn, background: 'transparent', color: '#7a6f65', border: '1.5px solid #e2dbd0', marginTop: '0.75rem' }}
            onClick={() => setStep(3)}>
            Skip for now (upload later)
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <h1 style={s.title}>Start your campaign</h1>
      <p style={s.sub}>Tell your story and start receiving support from around the world.</p>
      <div style={s.card}>
        {error && <div style={s.error}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={s.section}>
            <div style={s.sectionTitle}>Your story</div>
            <div style={s.field}>
              <label style={s.label}>Campaign title</label>
              <input style={s.input} value={form.title} required
                placeholder="e.g. Help me rebuild after Google layoff"
                onChange={e => update('title', e.target.value)} />
              <div style={s.hint}>Be specific — campaigns with clear titles get 60% more donations.</div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Your story</label>
              <textarea style={s.textarea} value={form.story} required
                placeholder="Tell donors what happened, who you are, what you need the money for, and what your plan is. Be honest and personal — your story is what moves people to donate."
                onChange={e => update('story', e.target.value)} />
              <div style={s.hint}>{form.story.length}/2000 characters (minimum 100)</div>
            </div>
          </div>

          <div style={s.section}>
            <div style={s.sectionTitle}>Employment details</div>
            <div style={s.row}>
              <div>
                <label style={s.label}>Former company</label>
                <input style={s.input} value={form.formerCompany} required
                  placeholder="e.g. Google" onChange={e => update('formerCompany', e.target.value)} />
              </div>
              <div>
                <label style={s.label}>Your role</label>
                <input style={s.input} value={form.formerRole} required
                  placeholder="e.g. Software Engineer" onChange={e => update('formerRole', e.target.value)} />
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Date of layoff</label>
              <input style={s.input} type="date" value={form.layoffDate} required
                max={new Date().toISOString().split('T')[0]}
                onChange={e => update('layoffDate', e.target.value)} />
            </div>
            <div style={s.field}>
              <label style={s.label}>LinkedIn profile URL (optional)</label>
              <input style={s.input} type="url" value={form.linkedinUrl}
                placeholder="https://linkedin.com/in/your-profile"
                onChange={e => update('linkedinUrl', e.target.value)} />
            </div>
          </div>

          <div style={s.section}>
            <div style={s.sectionTitle}>Funding goal</div>
            <div style={s.row}>
              <div>
                <label style={s.label}>Goal amount</label>
                <input style={s.input} type="number" value={form.goalAmount} required
                  min="1000" max="1000000" placeholder="e.g. 150000"
                  onChange={e => update('goalAmount', e.target.value)} />
              </div>
              <div>
                <label style={s.label}>Currency</label>
                <select style={s.input} value={form.currency} onChange={e => update('currency', e.target.value)}>
                  <option value="INR">INR — Indian Rupee</option>
                  <option value="USD">USD — US Dollar</option>
                  <option value="EUR">EUR — Euro</option>
                  <option value="GBP">GBP — British Pound</option>
                </select>
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Campaign deadline</label>
              <input style={s.input} type="date" value={form.deadline} required
                min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                onChange={e => update('deadline', e.target.value)} />
              <div style={s.hint}>We recommend 30–45 days for best results.</div>
            </div>
            <div style={s.row}>
              <div>
                <label style={s.label}>City</label>
                <input style={s.input} value={form.city} placeholder="Bengaluru"
                  onChange={e => update('city', e.target.value)} />
              </div>
              <div>
                <label style={s.label}>Country</label>
                <input style={s.input} value={form.country} placeholder="India"
                  onChange={e => update('country', e.target.value)} />
              </div>
            </div>
          </div>

          <div style={{ background: '#faf7f2', borderRadius: 10, padding: '1rem', marginBottom: '1.5rem', fontSize: '0.82rem', color: '#7a6f65', lineHeight: 1.7 }}>
            ℹ️ A platform fee of <strong>5%</strong> is deducted from successful donations to keep LayoffFund running. You receive the remaining 95%.
          </div>

          <button style={s.btn} type="submit" disabled={loading}>
            {loading ? 'Creating campaign...' : 'Create campaign & upload verification →'}
          </button>
        </form>
      </div>
    </div>
  );
}
